// Localhost-only project fallback used for manual E2E when Firebase Auth is not available.
// This never runs on production hosts.

const LOCAL_DEV_HOSTS = new Set(['localhost', '127.0.0.1']);
const LOCAL_DEV_PROJECTS_KEY = 'prowycena_local_dev_projects_v1';
const LOCAL_DEV_PROFILE_KEY = 'prowycena_local_dev_profile_v1';

export function isLocalDevProjectFallbackEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return LOCAL_DEV_HOSTS.has(window.location.hostname);
}

export function loadLocalDevProjects<T>(): T[] {
  return readJson<T[]>(LOCAL_DEV_PROJECTS_KEY, []);
}

export function saveLocalDevProjects<T>(projects: T[]): void {
  writeJson(LOCAL_DEV_PROJECTS_KEY, projects);
}

export function getLocalDevProjectById<T extends { id: string }>(id: string): T | null {
  return loadLocalDevProjects<T>().find((project) => project.id === id) ?? null;
}

export function getLocalDevProjectByToken<T extends { clientToken?: string }>(token: string): T | null {
  return loadLocalDevProjects<T>().find((project) => project.clientToken === token) ?? null;
}

export function upsertLocalDevProject<T extends { id: string }>(project: T): T[] {
  const projects = loadLocalDevProjects<T>();
  const nextProjects = [project, ...projects.filter((current) => current.id !== project.id)];
  saveLocalDevProjects(nextProjects);
  return nextProjects;
}

export function patchLocalDevProject<T extends { id: string }>(
  id: string,
  patch: Partial<T>
): T | null {
  const projects = loadLocalDevProjects<T>();
  const index = projects.findIndex((project) => project.id === id);

  if (index === -1) {
    return null;
  }

  const updatedProject = {
    ...projects[index],
    ...patch,
  };

  const nextProjects = [...projects];
  nextProjects[index] = updatedProject;
  saveLocalDevProjects(nextProjects);
  return updatedProject;
}

export function removeLocalDevProject<T extends { id: string }>(id: string): T[] {
  const nextProjects = loadLocalDevProjects<T>().filter((project) => project.id !== id);
  saveLocalDevProjects(nextProjects);
  return nextProjects;
}

export function loadLocalDevProfile<T>(fallback: T): T {
  return readJson<T>(LOCAL_DEV_PROFILE_KEY, fallback);
}

export function saveLocalDevProfile<T>(profile: T): void {
  writeJson(LOCAL_DEV_PROFILE_KEY, profile);
}

function readJson<T>(key: string, fallback: T): T {
  if (!isLocalDevProjectFallbackEnabled()) {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return fallback;
    }

    return JSON.parse(raw) as T;
  } catch (error) {
    console.warn('[localDevProjects] read failed:', error);
    return fallback;
  }
}

function writeJson<T>(key: string, value: T): void {
  if (!isLocalDevProjectFallbackEnabled()) {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn('[localDevProjects] write failed:', error);
  }
}
